import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * a11y (#55): The top-level dialog is triggered by a real `<button>` (ref="toggle")
 * instead of a `<details>`/`<summary>`, because `<summary>` must not be used to open a
 * modal dialog. The container (ref="details") is a plain element whose open state is
 * tracked with the `menu-open` class rather than the native `[open]` attribute, so this
 * component also manages the `scroll-lock` document attribute that dialog.js would
 * otherwise toggle on the native `<details>` `toggle` event. The nested submenus remain
 * native `<details>`/`<summary>` disclosure widgets and keep their native behaviour.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} details - The dialog container element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 * @property {HTMLButtonElement} toggle - The button that opens/closes the main drawer.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer', 'toggle'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.classList.contains('menu-open');
  }

  /**
   * Get the closest details element to the event target, falling back to the
   * top-level dialog container.
   * @param {Event | undefined} event
   * @returns {HTMLElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Get the control that toggles the given container: the trigger button for the
   * top-level dialog, or the native `<summary>` for a nested submenu.
   * @param {HTMLElement} details
   * @returns {HTMLElement | null}
   */
  #getControl(details) {
    if (details === this.refs.details) return this.refs.toggle;

    return details.querySelector('summary');
  }

  /**
   * Move focus to the first focusable element within a container.
   * Mirrors the focusable selector used by the shared focus trap.
   * @param {HTMLElement} container
   */
  #focusFirst(container) {
    const focusable = container.querySelector(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), input:not([type=hidden]):enabled, select:enabled, textarea:enabled"
    );
    if (focusable instanceof HTMLElement) focusable.focus();
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const control = this.#getControl(details);

    if (!control) return;

    // The top-level container is not a native `<details>`, so lock scroll manually.
    if (details === this.refs.details) {
      document.documentElement.setAttribute('scroll-lock', '');
    }

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus.
      // For the top-level dialog, scope the trap to the panel that carries
      // role="dialog"/aria-modal="true" (refs.menuDrawer) rather than the
      // container, which also holds the trigger button and backdrop outside
      // the dialog boundary. Submenus keep trapping within their own element.
      const trapTarget = details === this.refs.details ? this.refs.menuDrawer : details;
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(
        drawer || details,
        () => {
          trapFocus(trapTarget);
          // For the top-level dialog, move focus to the first focusable control
          // (the close button) instead of the panel container, so keyboard users
          // don't need an extra Tab press to reach the first interactive element.
          if (details === this.refs.details) {
            this.#focusFirst(trapTarget);
          }
        },
        { subtree: false }
      );
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLElement} details
   */
  #close(details) {
    const control = this.#getControl(details);

    if (!control) return;

    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Release the scroll lock when the top-level dialog closes.
    if (details === this.refs.details) {
      document.documentElement.removeAttribute('scroll-lock');
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details, control);
        if (details === this.refs.details) {
          removeTrapFocus();
          // Return focus to the element that opened the dialog (the trigger button).
          this.refs.toggle.focus();
        } else {
          // Returning from a submenu — re-trap within the dialog panel.
          trapFocus(this.refs.menuDrawer);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLElement} details - The container element holding the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open menu/submenu container to its original state
 *
 * @param {HTMLElement} element
 * @param {HTMLElement | null} [control] - The control whose aria-expanded should be reset
 */
function reset(element, control) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');

  // Only reset aria-expanded on native `<summary>` submenu controls. The top-level
  // dialog trigger is a `<button>` with aria-haspopup="dialog" and must never
}
